import { useEffect, useRef } from "react";
import type { HkTableState } from "../gen/bazivan/v1/table_pb";
import { signal, useSignal, type Signal } from "../net/signal";
import { HaftScene } from "../haft/scene";
import { HaftOnline } from "../haft/controller";
import { HaftResults } from "./HaftResults";
import { fa } from "./cards";

/**
 * میزِ هفت خبیث — ظرفِ ری‌اکتیِ صحنه‌ی امری.
 *
 * صحنه (HaftScene) و کنترلر (HaftOnline) پورتِ GameView و HaftOnlineController
 * یونیتی‌اند و DOM را خودشان می‌کشند؛ این کامپوننت فقط ظرف را می‌دهد و جدولِ
 * نتایجِ پایانِ دور را (ShowRoundOver). وضعیتِ سرور مستقیم به این کامپوننت
 * نمی‌رسد — از صفِ کنترلر می‌گذرد، تا انیمیشنِ قبلی تمام شده باشد.
 */
export function HaftTable({ state }: { state: HkTableState }) {
  const host = useRef<HTMLDivElement>(null);
  const sig = useRef<Signal<HaftOnline | null> | null>(null);
  if (!sig.current) sig.current = signal<HaftOnline | null>(null);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let c: HaftOnline | null = null;
    const scene = new HaftScene(el, state.seats.length || 4, {
      onCard: (i) => c?.clickCard(i),
      onDraw: () => c?.draw(),
      onPickSuit: (s) => c?.pickSuit(s),
      onSeat: (l) => c?.seatClick(l),
      onPickOffered: (i) => c?.pickOffered(i),
      onCallUno: () => c?.callUno(),
      onCatch: () => c?.catchUno(),
      seatName: (l) => c?.name(l) ?? "حریف",
    });
    c = new HaftOnline(scene);
    sig.current!.value = c;
    c.start();
    const ro = new ResizeObserver(() => scene.relayout());
    ro.observe(el);
    return () => { ro.disconnect(); c?.dispose(); scene.destroy(); sig.current!.value = null; };
    // صحنه یک بار ساخته می‌شود؛ وضعیت‌های بعدی از صفِ کنترلر می‌آیند.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="table tv-host haft" ref={host}>
      <Chrome sig={sig.current} />
    </div>
  );
}

function Chrome({ sig }: { sig: Signal<HaftOnline | null> }) {
  const c = useSignal(sig);
  if (!c) return null;
  return <ChromeInner c={c} />;
}

function ChromeInner({ c }: { c: HaftOnline }) {
  const over = useSignal(c.overlay);
  const raw = useSignal(c.raw);
  if (!over || !raw) return null;
  // نتایج: هم پایانِ دور (خبرِ گذرا تا دستِ بعد) و هم پایانِ مسابقه
  return <HaftResults state={raw} />;
}

/** نوارِ بالا: جریمه‌ی تجمعیِ هر کس تا سقف — کم‌تر بهتر. */
export function HaftScorebar({ state }: { state: HkTableState }) {
  return (
    <div className="scorebar">
      <div className="deckmedal" title="کارت‌های مانده در دسته">
        <span className="s">{fa(state.deckLeft)}</span>
        <span className="l">دسته</span>
      </div>
      <div className="rows">
        {state.scores.map((v, u) => (
          <div key={u} className={`srow ${!state.watching && u === state.me ? "us" : "them"}`}>
            <span className="who">{!state.watching && u === state.me ? "من" : state.seats[u]?.name || "بازیکن"}</span>
            <span className="bar"><i style={{ width: `${Math.min(100, (v / Math.max(1, state.targetScore)) * 100)}%` }} /></span>
            <b className="num">{fa(v)}</b>
          </div>
        ))}
      </div>
      <div className="match">
        <span className="l">سقف</span>
        <b>{fa(state.targetScore)}</b>
        {state.evil && <span className="l">خبیث</span>}
      </div>
    </div>
  );
}
